import { db } from "./dbClient";
import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import { useAuditLog } from "./useAuditLog";

function makeDownloadCSV(t) {
  return function downloadCSV(todos) {
    const rows = todos.map(todo =>
      `"${(todo.title||"").replace(/"/g,'""')}","${todo.done ? t('todo.stats.done') : t('common.pending')}","${todo.priority}","${new Date(todo.created_at||todo.createdAt).toLocaleDateString()}"`
    );
    const csv = [`${t('todo.headers.task')},${t('todo.headers.status')},${t('todo.headers.priority')},${t('todo.headers.created')}`, ...rows].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `todo_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };
}

const PRIORITY_CFG = {
  HIGH:   { color: "#FF6B6B", bg: "rgba(255,107,107,0.1)", labelKey: "common.high"   },
  MEDIUM: { color: "#F0B429", bg: "rgba(240,180,41,0.1)",  labelKey: "common.medium" },
  LOW:    { color: "#00E676", bg: "rgba(0,230,118,0.1)",   labelKey: "common.low"    },
};

export default function TodoPage({ team = [], company, currentUser }) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log } = useAuditLog(company, currentUser);
  const downloadCSV = makeDownloadCSV(t);
  const [todos,      setTodos]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [input,      setInput]      = useState("");
  const [priority,   setPriority]   = useState("MEDIUM");
  const [assigneeId, setAssigneeId] = useState(""); // "" = unassigned
  const [filter,     setFilter]     = useState("all");    // all | pending | done | mine
  const [filterP,    setFilterP]    = useState("all");
  const [filterUser, setFilterUser] = useState("all");
  const [editId,     setEditId]     = useState(null);
  const [editText,   setEditText]   = useState("");
  const [editAssign, setEditAssign] = useState("");
  const [viewMode,   setViewMode]   = useState("shared"); // shared | personal
  const inputRef = useRef(null);

  const isShared = viewMode === "shared" && company?.id;

  // ── Load todos ────────────────────────────────────────────────────────────
  const loadTodos = useCallback(() => {
    if (isShared) {
      setLoading(true);
      db.from("todos")
        .select("*")
        .eq("company_id", company.id)
        .order("created_at", false)
        .then(({ data, error }) => {
          if (error) {
            console.warn("[Todo] load error:", error.message);
            setTodos([]);
          } else {
            setTodos(data || []);
          }
          setLoading(false);
        })
        .catch(err => {
          console.warn("[Todo] load failed:", err.message);
          setTodos([]);
          setLoading(false);
        });
    } else {
      // Personal — localStorage only
      try { setTodos(JSON.parse(localStorage.getItem("crg_todos_personal") || "[]")); } catch { setTodos([]); }
      setLoading(false);
    }
  }, [isShared, company?.id]);

  useEffect(() => { loadTodos(); }, [loadTodos]);

  // Persist personal todos to localStorage
  useEffect(() => {
    if (!isShared) {
      try { localStorage.setItem("crg_todos_personal", JSON.stringify(todos)); } catch {}
    }
  }, [todos, isShared]);

  // ── Add todo ──────────────────────────────────────────────────────────────
  function addTodo() {
    const title = input.trim();
    if (!title) return;

    if (isShared) {
      db.from("todos").insert({
        title,
        priority,
        done: false,
        company_id: company.id,
        user_id: currentUser?.id,
      }).then(({ data, error }) => {
        if (error) {
          console.warn("[Todo] add error:", error.message);
          return;
        }
        if (data && data.length > 0) {
          const newTodo = data[0];
          setTodos(prev => [newTodo, ...prev]);
          log({
            action: "todo_created",
            module: "todo_management",
            entityId: newTodo.id,
            entityName: title,
            meta: { priority, status: "pending" }
          });
        }
      }).catch(err => {
        console.warn("[Todo] add failed:", err.message);
      });
    } else {
      const newTodo = {
        id: Date.now(),
        title,
        priority,
        done: false,
        created_at: new Date().toISOString(),
      };
      setTodos(prev => [newTodo, ...prev]);
    }
    setInput(""); setAssigneeId(""); inputRef.current?.focus();
  }

  // ── Toggle done ───────────────────────────────────────────────────────────
  function toggle(todo) {
    const newDone = !todo.done;
    if (isShared) {
      db.from("todos").update({ done: newDone }).eq("id", todo.id)
        .then(({ error }) => {
          if (!error) {
            log({
              action: "todo_status_changed",
              module: "todo_management",
              entityId: todo.id,
              entityName: todo.title,
              meta: { status: newDone ? "completed" : "pending" }
            });
          }
        })
        .catch(err => console.warn("[Todo] toggle failed:", err.message));
    }
    setTodos(prev => prev.map(t => t.id === todo.id ? { ...t, done: newDone } : t));
  }

  // ── Delete todo ───────────────────────────────────────────────────────────
  function remove(id) {
    const todo = todos.find(t => t.id === id);
    if (isShared) {
      db.from("todos").delete().eq("id", id)
        .then(({ error }) => {
          if (!error && todo) {
            log({
              action: "todo_deleted",
              module: "todo_management",
              entityId: id,
              entityName: todo.title,
              meta: { priority: todo.priority }
            });
          }
        })
        .catch(err => console.warn("[Todo] delete failed:", err.message));
    }
    setTodos(prev => prev.filter(t => t.id !== id));
  }

  // ── Edit todo ─────────────────────────────────────────────────────────────
  function startEdit(todo) {
    setEditId(todo.id);
    setEditText(todo.text);
    setEditAssign(todo.assignee_id || "");
  }

  function saveEdit(id) {
    const title = editText.trim();
    if (!title) return;

    const todo = todos.find(t => t.id === id);
    if (isShared) {
      db.from("todos").update({ title }).eq("id", id)
        .then(({ error }) => {
          if (!error && todo) {
            log({
              action: "todo_updated",
              module: "todo_management",
              entityId: id,
              entityName: title,
              meta: { oldTitle: todo.title }
            });
          }
        })
        .catch(err => console.warn("[Todo] edit failed:", err.message));
    }
    setTodos(prev => prev.map(t => t.id === id ? { ...t, title } : t));
    setEditId(null); setEditText(""); setEditAssign("");
  }

  // ── Clear done ────────────────────────────────────────────────────────────
  function clearDone() {
    const doneTodos = todos.filter(t => t.done);
    const doneIds = doneTodos.map(t => t.id);
    if (isShared && doneIds.length > 0) {
      doneIds.forEach(id => {
        db.from("todos").delete().eq("id", id)
          .then(({ error }) => {
            if (!error) {
              const todo = doneTodos.find(t => t.id === id);
              if (todo) {
                log({
                  action: "todo_deleted",
                  module: "todo_management",
                  entityId: id,
                  entityName: todo.title,
                  meta: { reason: "cleared_completed" }
                });
              }
            }
          })
          .catch(err => console.warn("[Todo] clear done failed:", err.message));
      });
    }
    setTodos(prev => prev.filter(t => !t.done));
  }

  // ── Filter ────────────────────────────────────────────────────────────────
  const filtered = todos.filter(t => {
    if (filter === "pending" && t.done) return false;
    if (filter === "done"    && !t.done) return false;
    if (filter === "mine"    && t.user_id !== currentUser?.id) return false;
    if (filterP !== "all"    && t.priority !== filterP) return false;
    return true;
  });

  const stats = {
    total:    todos.length,
    done:     todos.filter(t => t.done).length,
    pending:  todos.filter(t => !t.done).length,
    high:     todos.filter(t => t.priority === "HIGH" && !t.done).length,
    mine:     todos.filter(t => t.user_id === currentUser?.id).length,
  };
  const pct = stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0;

  const selectStyle = {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7,
    color: C.textMid, fontSize: 12, padding: "7px 10px",
    fontFamily: "'DM Mono', monospace", outline: "none", cursor: "pointer",
  };

  return (
    <div style={{ fontFamily: "'DM Mono', monospace", maxWidth: 780 }}>
      <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Mono:wght@300;400;500&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 700, margin: 0 }}>
            {t('todo.title')}
          </h1>
          <p style={{ color: C.textMid, margin: "4px 0 0", fontSize: 14 }}>
            {t('todo.tasksRemaining', { count: stats.pending })} · {pct}% {t('todo.complete')}
          </p>
        </div>

        {/* View mode toggle */}
        <div style={{ display: "flex", gap: 6 }}>
          {["personal", "shared"].map(mode => (
            <button key={mode} onClick={() => setViewMode(mode)} style={{
              padding: "7px 16px", borderRadius: 7, cursor: "pointer", fontSize: 12, fontWeight: 600,
              fontFamily: "'DM Mono', monospace", textTransform: "capitalize",
              background: viewMode === mode ? C.accentGlow : "transparent",
              border: `1px solid ${viewMode === mode ? C.accent : C.border}`,
              color: viewMode === mode ? C.accent : C.textMid,
            }}>
              {mode === "personal" ? `👤 ${t('todo.personal')}` : `👥 ${t('todo.team')}`}
            </button>
          ))}
        </div>
      </div>

      {/* Progress bar */}
      {stats.total > 0 && (
        <div style={{ marginBottom: 20, background: C.border, borderRadius: 4, height: 4, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: C.accent, borderRadius: 4, transition: "width 0.4s ease" }} />
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10, marginBottom: 24 }}>
        {[
          { label: t('todo.stats.total'),   value: stats.total,   color: C.accent  },
          { label: t('todo.stats.pending'), value: stats.pending, color: C.warn    },
          { label: t('todo.stats.done'),    value: stats.done,    color: C.success },
          { label: t('todo.stats.highPri'), value: stats.high,    color: "#FF6B6B" },
          { label: t('todo.stats.mine'),    value: stats.mine,    color: C.gold || "#F0B429" },
        ].map(s => (
          <div key={s.label} style={{ background: C.surface, border: `1px solid ${C.border}`, borderTop: `2px solid ${s.color}`, borderRadius: 10, padding: "12px 16px" }}>
            <div style={{ color: C.textDim, fontSize: 9, letterSpacing: "0.1em", marginBottom: 4 }}>{s.label.toUpperCase()}</div>
            <div style={{ color: s.color, fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Add task */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addTodo()}
            placeholder={t('todo.addPlaceholder')}
            style={{
              flex: 1, background: C.bg, border: `1px solid ${input ? C.accent : C.border}`,
              borderRadius: 7, color: C.text, padding: "9px 13px",
              fontFamily: "'DM Mono', monospace", fontSize: 13, outline: "none",
              transition: "border 0.15s",
            }}
          />
          <button onClick={addTodo} disabled={!input.trim()} style={{
            padding: "9px 20px", borderRadius: 7, border: "none",
            background: input.trim() ? C.accent : C.border,
            color: input.trim() ? C.bg : C.textDim,
            fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 700,
            cursor: input.trim() ? "pointer" : "not-allowed",
          }}>+ {t('todo.add')}</button>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          {/* Priority */}
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ color: C.textDim, fontSize: 11 }}>{t('todo.priorityLabel')}:</span>
            {Object.entries(PRIORITY_CFG).map(([key, cfg]) => (
              <button key={key} onClick={() => setPriority(key)} style={{
                padding: "3px 10px", borderRadius: 5, cursor: "pointer", fontSize: 10, fontWeight: 700,
                fontFamily: "'DM Mono', monospace",
                background: priority === key ? cfg.bg : "transparent",
                border: `1px solid ${priority === key ? cfg.color : C.border}`,
                color: priority === key ? cfg.color : C.textDim,
              }}>{t(cfg.labelKey)}</button>
            ))}
          </div>

          {/* Assign to — only in team mode */}
          {isShared && team.length > 0 && (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ color: C.textDim, fontSize: 11 }}>{t('todo.assignTo')}:</span>
              <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)} style={selectStyle}>
                <option value="">{t('common.unassigned')}</option>
                {team.map(m => {
                  const id = m.user_id || m.id;
                  const name = `${m.first_name || m.firstName || ""} ${m.last_name || m.lastName || ""}`.trim();
                  return <option key={id} value={id}>{name}</option>;
                })}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {(isShared ? ["all", "pending", "done", "mine"] : ["all", "pending", "done"]).map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 11,
            background: filter === f ? C.accentGlow : "transparent",
            border: `1px solid ${filter === f ? C.accent : C.border}`,
            color: filter === f ? C.accent : C.textMid,
            fontFamily: "'DM Mono', monospace", textTransform: "capitalize",
          }}>{f === "mine" ? `👤 ${t('todo.mine')}` : f === "all" ? t('common.all') : f === "pending" ? t('todo.stats.pending') : t('todo.stats.done')}</button>
        ))}

        <div style={{ width: 1, height: 18, background: C.border }} />

        {["all", "HIGH", "MEDIUM", "LOW"].map(p => (
          <button key={p} onClick={() => setFilterP(p)} style={{
            padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 11,
            background: filterP === p ? (p !== "all" ? PRIORITY_CFG[p]?.bg : C.accentGlow) : "transparent",
            border: `1px solid ${filterP === p ? (p !== "all" ? PRIORITY_CFG[p]?.color : C.accent) : C.border}`,
            color: filterP === p ? (p !== "all" ? PRIORITY_CFG[p]?.color : C.accent) : C.textMid,
            fontFamily: "'DM Mono', monospace",
          }}>{p === "all" ? t('common.all') : t(PRIORITY_CFG[p].labelKey)}</button>
        ))}

        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {stats.done > 0 && (
            <button onClick={clearDone} style={{ padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 11, background: "transparent", border: `1px solid ${C.danger}40`, color: C.danger, fontFamily: "'DM Mono', monospace" }}>
              🗑 {t('todo.clearDone')} ({stats.done})
            </button>
          )}
          {todos.length > 0 && (
            <button onClick={() => downloadCSV(todos)} style={{ padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 11, background: "transparent", border: `1px solid ${C.border}`, color: C.textMid, fontFamily: "'DM Mono', monospace" }}>
              ⬇ {t('common.export')}
            </button>
          )}
        </div>
      </div>

      {/* Todo list */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0", color: C.textDim, fontSize: 13 }}>{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "52px 0", color: C.textDim }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>✓</div>
          <div style={{ fontSize: 14 }}>{todos.length === 0 ? t('todo.noTasks') : t('todo.noMatch')}</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {filtered.map(todo => {
            const cfg = PRIORITY_CFG[todo.priority] || PRIORITY_CFG.MEDIUM;
            const isEditing = editId === todo.id;
            const isMyTodo = todo.user_id === currentUser?.id;

            return (
              <div key={todo.id} style={{
                display: "flex", alignItems: "flex-start", gap: 12,
                background: C.surface,
                border: `1px solid ${isMyTodo ? C.accent + "40" : todo.done ? C.border + "60" : C.border}`,
                borderLeft: `3px solid ${todo.done ? C.border : cfg.color}`,
                borderRadius: 9, padding: "12px 14px",
                opacity: todo.done ? 0.6 : 1,
                transition: "all 0.15s",
              }}>

                {/* Checkbox */}
                <button onClick={() => toggle(todo)} style={{
                  width: 22, height: 22, borderRadius: 6, flexShrink: 0, marginTop: 1,
                  background: todo.done ? C.success : "transparent",
                  border: `2px solid ${todo.done ? C.success : C.border}`,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.15s",
                }}>
                  {todo.done && <span style={{ color: C.bg, fontSize: 13, fontWeight: 900 }}>✓</span>}
                </button>

                {/* Content */}
                <div style={{ flex: 1 }}>
                  {isEditing ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <input
                        autoFocus
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") saveEdit(todo.id); if (e.key === "Escape") setEditId(null); }}
                        style={{ width: "100%", background: C.bg, border: `1px solid ${C.accent}`, borderRadius: 5, color: C.text, padding: "6px 10px", fontFamily: "'DM Mono', monospace", fontSize: 13, outline: "none", boxSizing: "border-box" }}
                      />
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => saveEdit(todo.id)} style={{ padding: "4px 12px", borderRadius: 5, cursor: "pointer", fontSize: 11, background: C.accent, border: "none", color: C.bg, fontFamily: "'DM Mono', monospace", fontWeight: 700 }}>{t('common.save')}</button>
                        <button onClick={() => setEditId(null)} style={{ padding: "4px 12px", borderRadius: 5, cursor: "pointer", fontSize: 11, background: "transparent", border: `1px solid ${C.border}`, color: C.textMid, fontFamily: "'DM Mono', monospace" }}>{t('common.cancel')}</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                        <span style={{
                          color: todo.done ? C.textDim : C.text, fontSize: 13,
                          textDecoration: todo.done ? "line-through" : "none",
                        }}>{todo.title}</span>
                        <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 3, background: cfg.bg, color: cfg.color, fontWeight: 700 }}>{t(cfg.labelKey)}</span>
                        {isMyTodo && !todo.done && (
                          <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 3, background: `${C.accent}18`, color: C.accent, fontWeight: 700 }}>← {t('todo.myTask')}</span>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 12, fontSize: 10, color: C.textDim }}>
                        <span>{new Date(todo.created_at || todo.createdAt).toLocaleDateString()}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Actions */}
                {!isEditing && (
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    {!todo.done && (
                      <button onClick={() => startEdit(todo)} style={{ padding: "4px 9px", borderRadius: 5, cursor: "pointer", fontSize: 10, background: "transparent", border: `1px solid ${C.border}`, color: C.textMid, fontFamily: "'DM Mono', monospace" }}>{t('common.edit')}</button>
                    )}
                    <button onClick={() => remove(todo.id)} style={{ padding: "4px 9px", borderRadius: 5, cursor: "pointer", fontSize: 10, background: "transparent", border: `1px solid ${C.danger}40`, color: C.danger, fontFamily: "'DM Mono', monospace" }}>✕</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}